import React, { useEffect, useState } from "react";
import { Link, Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, Edit3, FileText, Mail, LogOut, Eye, EyeOff, ArrowLeft, Save, Download, Upload, Gauge, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Eyebrow } from "@/components/Section";
import { MarkdownEditor } from "@/components/MarkdownEditor";

const AdminShell = ({ children }) => {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div data-testid="admin-shell" className="min-h-screen">
      <div className="border-b border-line bg-surface/40">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-6 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/admin" className="font-display font-black text-xl">Admin</Link>
            <nav className="hidden sm:flex gap-2">
              <Link to="/admin" data-testid="admin-nav-posts" className="px-3 py-2 font-mono text-xs uppercase tracking-[0.2em] text-muted hover:text-ink"><FileText size={12} className="inline mr-1.5"/>{t.admin.posts}</Link>
              <Link to="/admin/messages" data-testid="admin-nav-messages" className="px-3 py-2 font-mono text-xs uppercase tracking-[0.2em] text-muted hover:text-ink"><Mail size={12} className="inline mr-1.5"/>{t.admin.messages}</Link>
              <Link to="/admin/checks" data-testid="admin-nav-checks" className="px-3 py-2 font-mono text-xs uppercase tracking-[0.2em] text-muted hover:text-ink"><Gauge size={12} className="inline mr-1.5"/>Webseiten-Checks</Link>
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-mono text-xs text-muted hidden sm:inline">{user?.email}</span>
            <button
              onClick={async () => { await logout(); nav("/admin/login"); }}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-3 py-2 border border-line text-muted hover:text-ink hover:border-primary font-mono text-xs uppercase tracking-[0.2em]"
            >
              <LogOut size={12}/> {t.admin.sign_out}
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-12">{children}</div>
    </div>
  );
};

const PostsList = () => {
  const { t } = useI18n();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/posts/admin");
    setPosts(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Beitrag wirklich löschen?")) return;
    await api.delete(`/posts/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-10">
        <div>
          <Eyebrow>{t.admin.dashboard}</Eyebrow>
          <h1 className="mt-3 font-display font-black text-4xl tracking-tightest">{t.admin.posts}</h1>
        </div>
        <div className="flex items-center gap-2">
          <SyncButtons onChange={load} />
          <Link
            to="/admin/posts/new"
            data-testid="admin-new-post"
            className="inline-flex items-center gap-2 px-5 py-3 bg-primary text-white font-medium hover:bg-primary-hover transition-colors"
          >
            <Plus size={18}/> {t.admin.new_post}
          </Link>
        </div>
      </div>

      {loading ? (
        <p className="font-mono text-muted">{t.common.loading}</p>
      ) : posts.length === 0 ? (
        <p data-testid="admin-empty" className="font-mono text-muted">Keine Beiträge vorhanden.</p>
      ) : (
        <div className="border-l border-t border-line">
          {posts.map((p) => (
            <div key={p._id} className="grid grid-cols-12 border-r border-b border-line items-center gap-4 px-4 py-4 bg-surface/40">
              <div className="col-span-6">
                <div className="font-display font-bold text-ink">{p.title_de}</div>
                <div className="font-mono text-xs text-muted mt-1">/{p.slug}</div>
              </div>
              <div className="col-span-3 font-mono text-xs text-muted">
                {new Date(p.created_at).toLocaleDateString("de-DE")}
              </div>
              <div className="col-span-1 font-mono text-xs">
                {p.published ? <span className="text-primary inline-flex items-center gap-1"><Eye size={12}/>live</span> : <span className="text-muted inline-flex items-center gap-1"><EyeOff size={12}/>off</span>}
              </div>
              <div className="col-span-2 flex justify-end gap-2">
                <Link
                  to={`/admin/posts/${p._id}`}
                  data-testid={`admin-edit-${p.slug}`}
                  className="p-2 border border-line text-muted hover:text-ink hover:border-primary"
                >
                  <Edit3 size={14}/>
                </Link>
                <button
                  onClick={() => del(p._id)}
                  data-testid={`admin-delete-${p.slug}`}
                  className="p-2 border border-line text-muted hover:text-accent hover:border-accent"
                >
                  <Trash2 size={14}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AiDraftBox = ({ onDraft }) => {
  const [topic, setTopic] = useState("");
  const [notes, setNotes] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const generate = async () => {
    if (!topic.trim() || generating) return;
    setGenerating(true);
    setError("");
    try {
      const { data } = await api.post("/ai/blog-draft", { topic: topic.trim(), notes: notes.trim() || null }, { timeout: 300000 });
      onDraft(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Entwurf fehlgeschlagen. Bitte erneut versuchen.");
    }
    setGenerating(false);
  };

  return (
    <div className="border border-line bg-soft p-6 mb-8 max-w-4xl" data-testid="ai-draft-box">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-primary mb-3">KI-Blog-Assistent · Claude Opus 4.8</div>
      <div className="grid grid-cols-1 gap-3">
        <input
          data-testid="ai-draft-topic"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="Thema, z. B. „Veeam Backup CVE 2026 – Backup, das zurückbeißt“"
          className="w-full bg-white border border-line px-4 py-3 focus:border-primary outline-none"
        />
        <textarea
          data-testid="ai-draft-notes"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optionale Stichpunkte / Fakten / CVE-Nummern…"
          className="w-full bg-white border border-line px-4 py-3 focus:border-primary outline-none resize-none"
        />
        <div className="flex items-center gap-4">
          <button
            type="button"
            data-testid="ai-draft-generate"
            onClick={generate}
            disabled={generating || !topic.trim()}
            className="inline-flex items-center gap-2 px-6 py-3 bg-ink text-white font-medium hover:bg-ink/85 disabled:opacity-50"
          >
            {generating ? "Claude schreibt… (1–3 Min.)" : "Entwurf generieren (DE + EN)"}
          </button>
          {error && <span className="text-sm text-red-600" data-testid="ai-draft-error">{error}</span>}
        </div>
        <p className="font-mono text-[10px] text-muted">Der Entwurf füllt die Felder unten – prüfen, anpassen, speichern. Nichts wird automatisch veröffentlicht.</p>
      </div>
    </div>
  );
};

const PostEditor = () => {
  const { t } = useI18n();
  const { id } = useParams();
  const isNew = id === "new";
  const nav = useNavigate();
  const [form, setForm] = useState({
    title_de: "", title_en: "", excerpt_de: "", excerpt_en: "",
    content_de: "", content_en: "", cover_image: "", tags: "", published: true,
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get(`/posts/${id}`).then(({ data }) => {
      setForm({
        title_de: data.title_de || "", title_en: data.title_en || "",
        excerpt_de: data.excerpt_de || "", excerpt_en: data.excerpt_en || "",
        content_de: data.content_de || "", content_en: data.content_en || "",
        cover_image: data.cover_image || "", tags: (data.tags || []).join(", "),
        published: !!data.published,
      });
      setLoading(false);
    });
  }, [id, isNew]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title_de: form.title_de, title_en: form.title_en,
      excerpt_de: form.excerpt_de, excerpt_en: form.excerpt_en,
      content_de: form.content_de, content_en: form.content_en,
      cover_image: form.cover_image, published: form.published,
      tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
    };
    if (isNew) {
      await api.post("/posts", payload);
    } else {
      await api.put(`/posts/${id}`, payload);
    }
    setSaving(false);
    nav("/admin");
  };

  if (loading) return <p className="font-mono text-muted">{t.common.loading}</p>;

  return (
    <div>
      <Link to="/admin" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-muted hover:text-ink mb-6">
        <ArrowLeft size={14}/> {t.admin.posts}
      </Link>
      <h1 className="font-display font-black text-3xl tracking-tightest mb-8">{isNew ? t.admin.new_post : t.admin.edit_post}</h1>

      <AiDraftBox onDraft={(d) => setForm((f) => ({
        ...f,
        title_de: d.title_de || f.title_de,
        title_en: d.title_en || f.title_en,
        excerpt_de: d.excerpt_de || f.excerpt_de,
        excerpt_en: d.excerpt_en || f.excerpt_en,
        content_de: d.content_de || f.content_de,
        content_en: d.content_en || f.content_en,
        tags: (d.tags || []).join(", ") || f.tags,
      }))} />

      <form onSubmit={save} className="space-y-5 max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FormField label={t.admin.title_de}>
            <input data-testid="post-title-de" required value={form.title_de} onChange={(e) => change("title_de", e.target.value)} className="w-full bg-bg border border-line px-4 py-3 focus:border-primary outline-none"/>
          </FormField>
          <FormField label={t.admin.title_en}>
            <input data-testid="post-title-en" value={form.title_en} onChange={(e) => change("title_en", e.target.value)} className="w-full bg-bg border border-line px-4 py-3 focus:border-primary outline-none"/>
          </FormField>
          <FormField label={t.admin.excerpt_de}>
            <textarea data-testid="post-excerpt-de" rows={2} value={form.excerpt_de} onChange={(e) => change("excerpt_de", e.target.value)} className="w-full bg-bg border border-line px-4 py-3 focus:border-primary outline-none resize-none"/>
          </FormField>
          <FormField label={t.admin.excerpt_en}>
            <textarea data-testid="post-excerpt-en" rows={2} value={form.excerpt_en} onChange={(e) => change("excerpt_en", e.target.value)} className="w-full bg-bg border border-line px-4 py-3 focus:border-primary outline-none resize-none"/>
          </FormField>
        </div>
        <FormField label={t.admin.content_de}>
          <MarkdownEditor
            value={form.content_de}
            onChange={(v) => change("content_de", v)}
            testId="content-de"
            rows={14}
          />
        </FormField>
        <FormField label={t.admin.content_en}>
          <MarkdownEditor
            value={form.content_en}
            onChange={(v) => change("content_en", v)}
            testId="content-en"
            rows={14}
          />
        </FormField>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FormField label={t.admin.cover}>
            <input data-testid="post-cover" value={form.cover_image} onChange={(e) => change("cover_image", e.target.value)} placeholder="https://…" className="w-full bg-bg border border-line px-4 py-3 focus:border-primary outline-none"/>
          </FormField>
          <FormField label={t.admin.tags}>
            <input data-testid="post-tags" value={form.tags} onChange={(e) => change("tags", e.target.value)} placeholder="Security, Backup" className="w-full bg-bg border border-line px-4 py-3 focus:border-primary outline-none"/>
          </FormField>
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={form.published} onChange={(e) => change("published", e.target.checked)} data-testid="post-published" className="w-4 h-4 accent-[#0055FF]"/>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted">{t.admin.published}</span>
        </label>
        <div className="flex gap-3 pt-4">
          <button type="submit" disabled={saving} data-testid="post-save" className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-medium hover:bg-primary-hover disabled:opacity-50">
            <Save size={16}/> {saving ? t.common.loading : t.admin.save}
          </button>
          <Link to="/admin" className="inline-flex items-center gap-2 px-6 py-3 border border-line text-muted hover:text-ink">{t.admin.cancel}</Link>
        </div>
      </form>
    </div>
  );
};

const Messages = () => {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/contact");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => { await api.patch(`/contact/${id}/read`); load(); };
  const del = async (id) => { if (confirm("Nachricht löschen?")) { await api.delete(`/contact/${id}`); load(); } };

  return (
    <div>
      <Eyebrow>{t.admin.dashboard}</Eyebrow>
      <h1 className="mt-3 font-display font-black text-4xl tracking-tightest mb-10">{t.admin.messages}</h1>
      {loading ? <p className="font-mono text-muted">{t.common.loading}</p> :
        items.length === 0 ? <p data-testid="admin-no-messages" className="font-mono text-muted">Keine Nachrichten.</p> :
        <div className="space-y-4">
          {items.map((m) => (
            <div key={m._id} data-testid={`message-${m._id}`} className={`border ${m.read ? "border-line" : "border-primary/50"} bg-surface p-6`}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <span className="font-display font-bold text-lg">{m.name}</span>
                  <a href={`mailto:${m.email}`} className="font-mono text-xs text-primary">{m.email}</a>
                  {m.phone && <span className="font-mono text-xs text-muted">{m.phone}</span>}
                  {!m.read && <span className="font-mono text-[10px] uppercase tracking-[0.25em] bg-accent text-ink px-2 py-0.5">neu</span>}
                </div>
                <span className="font-mono text-xs text-muted">{new Date(m.created_at).toLocaleString("de-DE")}</span>
              </div>
              {m.subject && <div className="font-mono text-xs uppercase tracking-[0.25em] text-muted mb-3">{m.subject}</div>}
              <p className="text-ink whitespace-pre-wrap leading-relaxed">{m.message}</p>
              <div className="mt-4 flex gap-2">
                {!m.read && <button onClick={() => markRead(m._id)} className="px-3 py-1.5 border border-line font-mono text-xs uppercase tracking-[0.2em] text-muted hover:text-ink">Als gelesen markieren</button>}
                <button onClick={() => del(m._id)} className="px-3 py-1.5 border border-line font-mono text-xs uppercase tracking-[0.2em] text-muted hover:text-accent hover:border-accent">Löschen</button>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
};

export const AdminApp = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center font-mono text-muted">Lädt …</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <AdminShell>
      <Routes>
        <Route index element={<PostsList />} />
        <Route path="posts/:id" element={<PostEditor />} />
        <Route path="messages" element={<Messages />} />
        <Route path="checks" element={<WebsiteChecks />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AdminShell>
  );
};

const WebsiteChecks = () => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [expanded, setExpanded] = React.useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/website-checks");
    setItems(data);
    setLoading(false);
  };
  React.useEffect(() => { load(); }, []);

  const markContacted = async (id) => { await api.patch(`/website-checks/${id}/contacted`); load(); };
  const del = async (id) => { if (confirm("Eintrag löschen?")) { await api.delete(`/website-checks/${id}`); load(); } };

  return (
    <div>
      <Eyebrow>Leads</Eyebrow>
      <h1 className="mt-3 font-display font-black text-4xl tracking-tightest mb-10">Webseiten-Checks</h1>
      {loading ? <p className="font-mono text-muted">Lädt …</p> :
        items.length === 0 ? <p data-testid="admin-no-checks" className="font-mono text-muted">Noch keine Checks. Sobald jemand auf /webseiten-check eine Anfrage stellt, taucht sie hier auf.</p> :
        <div className="space-y-3">
          {items.map((c) => {
            const grade = c.grade || "-";
            const score = c.score || 0;
            const scoreColor = score >= 70 ? "text-primary" : score >= 50 ? "text-cyan-600" : "text-accent";
            return (
              <div key={c._id} data-testid={`check-${c._id}`} className={`border ${c.contacted ? "border-line" : "border-primary/40"} bg-surface`}>
                <div className="grid grid-cols-12 gap-4 items-center p-4">
                  <div className={`col-span-2 sm:col-span-1 font-display font-black text-3xl ${scoreColor} text-center`}>
                    {score}
                  </div>
                  <div className="col-span-10 sm:col-span-5">
                    <div className="font-display font-bold text-ink break-all">{c.final_url || c.url}</div>
                    <div className="font-mono text-xs text-muted mt-1">Note {grade} · {c.load_ms ? `${c.load_ms} ms` : "—"} {c.ok ? "" : "· Check fehlgeschlagen"}</div>
                  </div>
                  <div className="col-span-8 sm:col-span-3">
                    <a href={`mailto:${c.email}`} className="font-mono text-xs text-primary block truncate">{c.email}</a>
                    {c.name && <div className="text-sm text-ink truncate">{c.name}{c.company ? ` · ${c.company}` : ""}</div>}
                  </div>
                  <div className="col-span-4 sm:col-span-2 font-mono text-[10px] text-muted text-right">
                    {new Date(c.created_at).toLocaleString("de-DE")}
                    {!c.contacted && <div className="mt-1 inline-block bg-accent text-ink px-2 py-0.5 uppercase tracking-[0.2em]">neu</div>}
                  </div>
                  <div className="col-span-12 sm:col-span-1 flex justify-end gap-2">
                    <button onClick={() => setExpanded(expanded === c._id ? null : c._id)} className="p-2 border border-line text-muted hover:text-ink hover:border-primary"><Eye size={14}/></button>
                  </div>
                </div>
                {expanded === c._id && (
                  <div className="border-t border-line p-4 bg-bg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.entries(c.checks || {}).map(([k, v]) => (
                        <div key={k} className={`flex items-start gap-2 p-3 border ${v.ok ? "border-line bg-white" : "border-accent/30 bg-accent/5"}`}>
                          <span className={v.ok ? "text-primary" : "text-accent"}>
                            {v.ok ? <CheckCircle2 size={14}/> : <span className="font-mono">!</span>}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="font-display font-bold text-sm text-ink">{v.label}</div>
                            <div className="font-mono text-[10px] text-muted truncate">{String(v.value)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex gap-2 flex-wrap">
                      {!c.contacted && <button onClick={() => markContacted(c._id)} className="px-3 py-1.5 border border-line font-mono text-xs uppercase tracking-[0.2em] text-muted hover:text-ink">Als kontaktiert markieren</button>}
                      <a href={`mailto:${c.email}?subject=Ihr%20Webseiten-Check%20f%C3%BCr%20${encodeURIComponent(c.url)}`} className="px-3 py-1.5 border border-line font-mono text-xs uppercase tracking-[0.2em] text-primary hover:bg-primary hover:text-white">E-Mail antworten</a>
                      <button onClick={() => del(c._id)} className="px-3 py-1.5 border border-line font-mono text-xs uppercase tracking-[0.2em] text-muted hover:text-accent hover:border-accent">Löschen</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      }
    </div>
  );
};

const FormField = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted">{label}</span>
    <div className="mt-2">{children}</div>
  </label>
);

const SyncButtons = ({ onChange }) => {
  const fileRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);

  const doExport = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/posts/export/all");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `it-servicepoint-posts-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  };

  const doImport = async (file) => {
    setBusy(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const posts = parsed.posts || parsed;
      const overwrite = confirm(
        `${posts.length} Beiträge in der Datei. Vorhandene Beiträge mit gleichem Slug überschreiben? (OK = überschreiben, Abbrechen = nur neue importieren)`
      );
      const { data } = await api.post("/posts/import", { posts, overwrite });
      alert(`Import fertig\nNeu: ${data.created}\nÜberschrieben: ${data.updated}\nÜbersprungen: ${data.skipped}`);
      onChange?.();
    } catch (e) {
      alert("Import fehlgeschlagen: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={doExport}
        disabled={busy}
        data-testid="admin-export-posts"
        className="inline-flex items-center gap-2 px-4 py-3 border border-line bg-white text-muted hover:text-primary hover:border-primary transition-colors disabled:opacity-50"
        title="Alle Beiträge als JSON-Datei herunterladen"
      >
        <Download size={16} /> <span className="hidden sm:inline">Export</span>
      </button>
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && doImport(e.target.files[0])}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        data-testid="admin-import-posts"
        className="inline-flex items-center gap-2 px-4 py-3 border border-line bg-white text-muted hover:text-primary hover:border-primary transition-colors disabled:opacity-50"
        title="Beiträge aus JSON-Datei importieren"
      >
        <Upload size={16} /> <span className="hidden sm:inline">Import</span>
      </button>
    </>
  );
};
